// frontend/src/hooks/chat/useChatQueries.ts
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  getChannelByEpisodeChatChannelsByEpisodeEpisodeIdGet,
  getChatChannel,
  getChatDirectory,
  getChatInbox,
  getChatMessages,
  listRoles,
  searchChatChannelMembers,
  resolveChatLinkTitles,
} from '../../services/api';
import type {
  ChannelHeader,
  ChannelMemberOption,
  DirectoryRow,
  InboxRow,
  MessageOut,
} from '../../services/api';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { FeatureFlag } from '../../constants/featureFlags';
import { useStore } from '../../store';

export const MESSAGE_PAGE_SIZE = 50;
const INBOX_STALE_TIME_MS = 30_000;

export const chatKeys = {
  all: ['chat'] as const,
  inbox: () => ['chat', 'inbox'] as const,
  channel: (id: string) => ['chat', 'channel', id] as const,
  messages: (id: string) => ['chat', 'messages', id] as const,
  directory: (q: string) => ['chat', 'directory', q] as const,
  members: (channelId: string, q: string) =>
    ['chat', 'members', channelId, q] as const,
  // Sorted: the same member set is one conversation whatever order it was picked in.
  conversation: (userIds: string[]) =>
    ['chat', 'conversation', [...userIds].sort().join(',')] as const,
  announcerRole: () => ['chat', 'announcer-role'] as const,
  announcers: (groupId: string) => ['chat', 'announcers', groupId] as const,
  send: () => ['chat', 'send'] as const,
  reaction: (channelId: string) => ['chat', 'reaction', channelId] as const,
  careTeam: (episodeId: string) => ['chat', 'care-team', episodeId] as const,
  linkTitle: (url: string) => ['chat', 'link-title', url] as const,
};

/** The reader's own title for one Roger address (EMR-2675). Never stored. */
export function useLinkTitle(url: string) {
  return useQuery({
    queryKey: chatKeys.linkTitle(url),
    queryFn: async () => {
      const { data } = await resolveChatLinkTitles({
        body: { urls: [url] },
        throwOnError: true,
      });
      return data[0];
    },
    // Short: the title depends on what this reader may see right now.
    staleTime: INBOX_STALE_TIME_MS,
    retry: 1,
  });
}

export function useInbox() {
  return useQuery({
    queryKey: chatKeys.inbox(),
    queryFn: async () => {
      // Read here, not in the key: one shared cache entry, so mark-read and
      // the badge keep updating it. The menu invalidates it on a flip.
      const includeAnnouncements = useStore.getState().chatShowAllAnnouncements;
      const { data } = await getChatInbox({
        ...(includeAnnouncements
          ? { query: { include_announcements: true } }
          : {}),
        throwOnError: true,
      });
      return (data ?? []) as InboxRow[];
    },
    staleTime: INBOX_STALE_TIME_MS,
  });
}

/** Source of truth for the badge and the tab title: the inbox rows. */
export function useUnreadTotal(): number {
  const { data } = useInbox();
  return (data ?? []).reduce((sum, r) => sum + (r.unread_count ?? 0), 0);
}

export function useChannel(channelId: string | null) {
  return useQuery({
    queryKey: chatKeys.channel(channelId ?? ''),
    queryFn: async () => {
      const { data } = await getChatChannel({
        path: { channel_id: channelId! },
        throwOnError: true,
      });
      return data as ChannelHeader;
    },
    enabled: !!channelId,
  });
}

export function useCareTeamChannel(episodeId: string | undefined) {
  const hasChat = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.CHAT, 'workos')
  );
  return useQuery({
    queryKey: chatKeys.careTeam(episodeId ?? ''),
    queryFn: async () => {
      try {
        const response =
          await getChannelByEpisodeChatChannelsByEpisodeEpisodeIdGet({
            path: { episode_id: episodeId as string },
            throwOnError: true,
          });
        return response.data;
      } catch (error) {
        // A 404 or a 403 both mean "no button" and stay silent, the way a
        // stale session's flag-off query stays silent — returned as a
        // successful "no channel" rather than a query error, so the shared
        // query cache's error logging (services/queryClient.ts) never fires
        // for the expected case. Anything else is logged (AGENTS.md: never
        // show a raw server error, but do log it) and left to fail the query.
        const status = getHttpStatus(error);
        if (status === 404 || status === 403) return null;
        console.error('Failed to load the care-team channel', error);
        throw error;
      }
    },
    enabled: hasChat && Boolean(episodeId),
    retry: false,
    staleTime: INBOX_STALE_TIME_MS,
  });
}

export function useMessages(channelId: string | null) {
  const queryClient = useQueryClient();
  return useInfiniteQuery({
    queryKey: chatKeys.messages(channelId ?? ''),
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await getChatMessages({
        path: { channel_id: channelId! },
        query: {
          limit: MESSAGE_PAGE_SIZE,
          ...(pageParam ? { before: pageParam } : {}),
        },
        throwOnError: true,
      });
      const rows = (data ?? []) as MessageOut[];
      confirmFetched(queryClient, channelId!, rows);
      return rows;
    },
    // `before=` pages return newest first, so the oldest seq of the last page
    // is the cursor for the page before it.
    getNextPageParam: last =>
      last.length < MESSAGE_PAGE_SIZE
        ? undefined
        : Math.min(...last.map(m => m.seq)),
    enabled: !!channelId,
  });
}

/** Oldest first, one row per seq. Frames and pages can overlap; seq de-dupes. */
export function flattenMessages(
  pages: MessageOut[][] | undefined
): MessageOut[] {
  const bySeq = new Map<number, MessageOut>();
  for (const page of pages ?? []) for (const m of page) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

export type MessagePages =
  { pages: MessageOut[][]; pageParams: unknown[] } | undefined;

/**
 * Merge arrivals into the newest page (`pages[0]`), never as a new trailing page.
 * `useInfiniteQuery` reads `hasNextPage` and the next cursor from the LAST page,
 * so a trailing one-row arrival page would end paging of older history for the
 * rest of the session, and would leave `pageParams` shorter than `pages`.
 */
export function mergeIntoNewestPage(
  old: MessagePages,
  arrivals: MessageOut[]
): MessagePages {
  if (!old) return { pages: [arrivals], pageParams: [undefined] };
  const cached = new Set(old.pages.flat().map(m => m.seq));
  const fresh = arrivals.filter(m => !cached.has(m.seq));
  if (fresh.length === 0) return old;
  const [newest = [], ...older] = old.pages;
  return { ...old, pages: [[...newest, ...fresh], ...older] };
}

/**
 * Merge arrivals into a channel's messages cache, and merge them again when a
 * fetch that was already running settles. An infinite query builds its result
 * from the cache snapshot taken when the fetch started (an older page, or a
 * refetch), so without the second merge that result would drop an arrival that
 * landed while it ran.
 */
export function mergeArrivals(
  queryClient: QueryClient,
  channelId: string,
  arrivals: MessageOut[]
): void {
  const queryKey = chatKeys.messages(channelId);
  const apply = () =>
    queryClient.setQueryData(queryKey, (old: MessagePages) =>
      mergeIntoNewestPage(old, arrivals)
    );
  apply();
  const cache = queryClient.getQueryCache();
  const query = cache.find({ queryKey, exact: true });
  if (!query || query.state.fetchStatus === 'idle') return;
  const unsubscribe = cache.subscribe(event => {
    if (event.query !== query) return;
    if (event.type === 'removed') return unsubscribe();
    if (query.state.fetchStatus !== 'idle') return;
    unsubscribe();
    apply();
  });
}

/**
 * Seqs that are in the cache only because a send response put them there. A
 * send response is one row, not a list, so a message another person sent just
 * before it may not be cached yet: these seqs must not move the fetch or read
 * cursor. A list fetch that returns a seq removes it from the set.
 */
const sendOnlySeqs = new WeakMap<QueryClient, Map<string, Set<number>>>();

export function sendOnlySet(queryClient: QueryClient, channelId: string) {
  const byChannel =
    sendOnlySeqs.get(queryClient) ?? new Map<string, Set<number>>();
  sendOnlySeqs.set(queryClient, byChannel);
  const seqs = byChannel.get(channelId) ?? new Set<number>();
  byChannel.set(channelId, seqs);
  return seqs;
}

/**
 * Messages queries that a list fetch has completed for. Keyed on the query
 * itself, so an evicted cache starts again as "not fetched".
 */
const fetchedHistories = new WeakSet<object>();

function messagesQuery(queryClient: QueryClient, channelId: string) {
  return queryClient
    .getQueryCache()
    .find({ queryKey: chatKeys.messages(channelId), exact: true });
}

function confirmFetched(
  queryClient: QueryClient,
  channelId: string,
  rows: MessageOut[]
): void {
  const seqs = sendOnlySet(queryClient, channelId);
  for (const row of rows) seqs.delete(row.seq);
  const query = messagesQuery(queryClient, channelId);
  if (query) fetchedHistories.add(query);
}

/**
 * Whether a list fetch has completed for the channel's cached history. Until
 * one has, the cache holds at most send responses, and `after=0` would drain
 * the channel from its oldest row.
 */
export function hasFetchedHistory(
  queryClient: QueryClient,
  channelId: string
): boolean {
  const query = messagesQuery(queryClient, channelId);
  return !!query && fetchedHistories.has(query);
}

/**
 * The newest seq that a list fetch has returned for the channel: the `after=`
 * cursor for a live fetch and the seq to mark read. `rows` are oldest first.
 * Returns 0 when no fetched row is cached.
 */
export function lastFetchedSeq(
  queryClient: QueryClient,
  channelId: string,
  rows: MessageOut[]
): number {
  const seqs = sendOnlySet(queryClient, channelId);
  for (let i = rows.length - 1; i >= 0; i--) {
    if (!seqs.has(rows[i].seq)) return rows[i].seq;
  }
  return 0;
}

export function useDirectory(query: string) {
  return useQuery({
    queryKey: chatKeys.directory(query),
    queryFn: async () => {
      const { data } = await getChatDirectory({
        query: { q: query, limit: 50 },
        throwOnError: true,
      });
      return (data ?? []) as DirectoryRow[];
    },
    placeholderData: keepPreviousData,
  });
}

/** EMR-2560: members of one channel for the @ list. `null` makes no request. */
export function useChannelMemberSearch(
  channelId: string,
  query: string | null
) {
  return useQuery({
    queryKey: chatKeys.members(channelId, query ?? ''),
    queryFn: async () => {
      try {
        const { data } = await searchChatChannelMembers({
          path: { channel_id: channelId },
          query: { q: query ?? '' },
          throwOnError: true,
        });
        return (data ?? []) as ChannelMemberOption[];
      } catch (error) {
        console.error('Chat member search failed', error);
        throw error;
      }
    },
    enabled: query !== null,
    placeholderData: keepPreviousData,
  });
}

/**
 * The single GROUP-scope role (EMR-1876). Fetched once per session. The caller
 * needs the failure too: without the role id no announcer can be added.
 */
export function useAnnouncerRoleId(enabled: boolean): {
  roleId: string | undefined;
  isError: boolean;
} {
  const { data, isError } = useQuery({
    queryKey: chatKeys.announcerRole(),
    queryFn: async () => {
      const { data } = await listRoles({
        query: { scope: 'group' },
        throwOnError: true,
      });
      return data ?? [];
    },
    enabled,
    staleTime: Infinity,
  });
  return { roleId: data?.[0]?.id, isError };
}

/**
 * Both throttles are keyed on the QueryClient, not held in a module variable:
 * a module variable survives between tests in one file, so the second test's
 * refetch would be pushed onto a timer and its mark-read dropped.
 */
const inboxRefetchState = new WeakMap<
  QueryClient,
  { lastAt: number; timer?: ReturnType<typeof setTimeout> }
>();

/**
 * Refetch the inbox at most once a second. A burst of frames (an announcement
 * fan-out) must cost one request, not one per frame.
 */
export function refetchInboxCoalesced(queryClient: QueryClient): void {
  const s = inboxRefetchState.get(queryClient) ?? { lastAt: 0 };
  inboxRefetchState.set(queryClient, s);
  const since = Date.now() - s.lastAt;
  if (since >= 1000) {
    s.lastAt = Date.now();
    void queryClient.refetchQueries({ queryKey: chatKeys.inbox() });
    return;
  }
  if (s.timer) return;
  s.timer = setTimeout(() => {
    s.timer = undefined;
    s.lastAt = Date.now();
    void queryClient.refetchQueries({ queryKey: chatKeys.inbox() });
  }, 1000 - since);
}

/**
 * Fetch one page after `afterSeq`, merge it into the channel's cache, and
 * return how many rows it held. A full page means more may follow.
 */
export async function appendMessagesAfter(
  queryClient: QueryClient,
  channelId: string,
  afterSeq: number
): Promise<number> {
  const { data } = await getChatMessages({
    path: { channel_id: channelId },
    query: { after: afterSeq, limit: MESSAGE_PAGE_SIZE },
    throwOnError: true,
  });
  const arrivals = (data ?? []) as MessageOut[];
  if (arrivals.length === 0) return 0;
  confirmFetched(queryClient, channelId, arrivals);
  mergeArrivals(queryClient, channelId, arrivals);
  return arrivals.length;
}
